import React, { useState } from 'react';
import { NavBar } from '@/components/navbar';
import { Footer } from '@/components/SiteFooter';
import { Helmet } from 'react-helmet-async';
import { Container } from '@/components/container';
import { Background } from '@/components/background';
import { SmallHero } from '@/components/ui/small-hero-with-cta';
import { Badge } from '@/components/ui/badge';
import { Heading } from '@/components/heading';
import { Subheading } from '@/components/subheading';
import { BarChart4, ClipboardCheck, Layers, Shield, Sparkles } from 'lucide-react';
import ComplianceAIForm from '@/components/compliance-ai/ComplianceAIForm';
import ComplianceAIResponse from '@/components/compliance-ai/ComplianceAIResponse';
import { ComplianceFormData } from '@/types/compliance';
import { mockComplianceAiResponse } from '@/mocks/complianceAiResponse';
import { API_CONFIG } from '@/constants/env.ts';
import LoadingAnimation from '@/components/ui/loading-animation';

const SmallCTAPage = () => {
  const [formData, setFormData] = useState<ComplianceFormData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [response, setResponse] = useState<any | null>(null);
  const [timeRemaining, setTimeRemaining] = useState(90); // 90 second countdown

  const handleFormSubmit = async (data: ComplianceFormData) => {
    setFormData(data);
    setIsLoading(true);
    setError(null);
    setTimeRemaining(90); // Reset timer to 90 seconds

    try {
      // Use the actual API endpoint from API_CONFIG
      const apiUrl = `${API_CONFIG.API_BASE_URL}/compliance-ai/`;
      console.log('Sending request to compliance AI API:', apiUrl);

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Origin: window.location.origin,
        },
        mode: 'cors',
        credentials: 'omit',
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        throw new Error(
          `API request failed with status ${response.status}: ${response.statusText}`
        );
      }

      let responseData = await response.json();

      console.log('API Response:', responseData);

      // Handle different response formats
      if (typeof responseData === 'string') {
        try {
          // Try to parse if it's a JSON string
          responseData = JSON.parse(responseData);
        } catch (e) {
          // If it's not valid JSON, wrap it in a content property
          responseData = { content: responseData };
        }
      }

      // Handle direct response object format (post-processed by backend)
      if (
        responseData.text !== undefined &&
        responseData.advice !== undefined &&
        responseData.diagram !== undefined
      ) {
        console.log('Got well-formed response from API with text/advice/diagram properties');

        // Fix potential diagram issues if it's a string (for backward compatibility)
        if (typeof responseData.diagram === 'string') {
          fixDiagramSyntax(responseData);
        }
      }
      // Handle nested content property
      else if (responseData.content && typeof responseData.content === 'string') {
        try {
          const parsed = JSON.parse(responseData.content);
          if (parsed.text || parsed.advice || parsed.diagram) {
            responseData = parsed;
            if (typeof responseData.diagram === 'string') {
              fixDiagramSyntax(responseData);
            }
          }
        } catch (e) {
          console.error('Failed to parse content string:', e);
        }
      }
      // Handle Python dict-like representation
      else if (
        typeof responseData === 'object' &&
        Object.keys(responseData).some(key => key.includes("'"))
      ) {
        console.log('Got Python dict-like response, converting to proper JSON');
        try {
          // Convert Python dict format to proper JSON object
          const fixedData = {};

          Object.keys(responseData).forEach(key => {
            // Remove single quotes and fix key names
            const cleanKey = key.replace(/'/g, '');
            fixedData[cleanKey] = responseData[key];
          });

          // Set the properly formatted data
          responseData = fixedData;

          // Fix potential diagram issues if it's a string
          if (typeof responseData.diagram === 'string') {
            fixDiagramSyntax(responseData);
          }

          console.log('Converted response:', responseData);
        } catch (e) {
          console.error('Failed to convert Python dict format:', e);
        }
      }

      console.log('Final processed response for component:', responseData);

      // Ensure we show the loading animation for at least 5 seconds
      const elapsedTime = 90 - timeRemaining;
      if (elapsedTime < 5) {
        // Wait until we've shown the animation for at least 5 seconds
        setTimeout(
          () => {
            setResponse(responseData);
            setIsLoading(false);
          },
          (5 - elapsedTime) * 1000
        );
      } else {
        setResponse(responseData);
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Error submitting form:', error);

      if (error instanceof Error) {
        console.log('Error details:', {
          name: error.name,
          message: error.message,
          stack: error.stack,
        });
        setError(error);
      } else {
        setError(new Error('An unknown error occurred'));
      }

      // Fallback to mock data if API fails
      if (API_CONFIG.USE_MOCK_API) {
        console.log('Falling back to mock data due to API error');
        setTimeout(() => {
          setResponse({
            ...mockComplianceAiResponse,
            title: 'Financial Marketing Compliance Assessment',
            summary:
              'Your financial marketing materials have been analyzed for regulatory compliance. Here are our recommendations to improve your compliance posture.',
          });
          setIsLoading(false);
        }, 1000);
      } else {
        setIsLoading(false);
      }
    }
  };

  // Helper function to fix diagram syntax issues
  function fixDiagramSyntax(dataObj) {
    if (!dataObj.diagram || typeof dataObj.diagram !== 'string') return;

    // Special case for the exact diagram format in the console log
    if (dataObj.diagram.includes('style F1 fill:#90EE90') && !dataObj.diagram.endsWith('end')) {
      console.log('Detected the exact diagram format from the console log');
      dataObj.diagram += '\n    end';
      console.log('Fixed the diagram by adding missing end tag');
      return;
    }

    // Count subgraph and end tags
    const subgraphCount = (dataObj.diagram.match(/subgraph/g) || []).length;
    const endCount = (dataObj.diagram.match(/end/g) || []).length;

    console.log(`Diagram analysis - Subgraph count: ${subgraphCount}, End count: ${endCount}`);

    // Add missing end tags
    if (subgraphCount > endCount) {
      for (let i = 0; i < subgraphCount - endCount; i++) {
        dataObj.diagram += ' end';
      }
    }

    // For diagrams with style declarations at the end
    if (dataObj.diagram.match(/style\s+[A-Z0-9]+\s+fill:#[0-9A-F]{6}$/i)) {
      console.log('Diagram ends with a style declaration, adding end tag');
      dataObj.diagram += '\n    end';
    }

    // Log the fixed diagram
    console.log('Fixed diagram:', dataObj.diagram);
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>Custom Compliance Workflows | Enterprise AI Solutions</title>
        <meta
          name="description"
          content="Custom AI-powered compliance workflows designed for your industry at up to 45% less than competitors. Reduce costs by 65%, slash error rates by 86%, and regain 240+ hours annually."
        />
      </Helmet>

      {!isLoading && <NavBar landingMode />}

      <main className="flex-grow">
        {!isLoading && !response && (
          <div className="relative">
            <div className="absolute inset-0 h-full w-full overflow-hidden">
              <Background />
            </div>

            <Container className="flex min-h-screen flex-col items-center justify-between pt-10">
              {/* Hero Section */}
              <SmallHero />

              {/* Features Section */}
              <div className="relative z-20 py-10 md:py-16 w-full">
                <Heading as="h2">Custom Compliance AI</Heading>
                <Subheading className="text-center">
                  Tailored workflows at enterprise scale, at costs that beat competitors
                </Subheading>

                <div className="relative z-20 mb-10 mt-12 rounded-xl">
                  {/* Background panel */}
                  <div className="absolute inset-0 bg-white/90 dark:bg-black/80 backdrop-blur-sm -z-10 rounded-xl shadow-lg border border-neutral-200 dark:border-neutral-800"></div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 py-10">
                    {features.map((feature, index) => (
                      <Feature key={feature.id} {...feature} index={index} />
                    ))}
                  </div>
                </div>
              </div>

              {/* Use Cases */}
              <div className="w-full py-12">
                <Heading as="h2">Enterprise Advantages</Heading>
                <Subheading className="text-center mb-10">
                  Cost-effective compliance automation that turns regulation into opportunity
                </Subheading>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  {useCases.map(useCase => (
                    <div
                      key={useCase.id}
                      className="p-6 rounded-lg border border-gray-200 bg-white/80 backdrop-blur-sm shadow-sm hover:shadow-md transition-all duration-300"
                    >
                      <div className="mb-4">
                        <Badge
                          variant={useCase.badgeVariant}
                          className="uppercase text-xs tracking-wider"
                        >
                          {useCase.badge}
                        </Badge>
                      </div>
                      <h3 className="text-xl font-medium mb-3">{useCase.title}</h3>
                      <p className="text-gray-600 mb-4">{useCase.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </Container>
          </div>
        )}

        {isLoading ? (
          <div className="fixed inset-0 bg-black/10 backdrop-blur-sm z-50 flex items-center justify-center">
            <div className="container mx-auto px-4">
              <div className="max-w-5xl mx-auto">
                <div className="bg-card p-8 rounded-2xl shadow-xl border border-border">
                  <LoadingAnimation
                    timeRemaining={timeRemaining}
                    setTimeRemaining={setTimeRemaining}
                    initialTime={90}
                  />
                </div>
              </div>
            </div>
          </div>
        ) : response ? (
          <section className="py-12 bg-background">
            <div className="container mx-auto px-4">
              <div className="max-w-5xl mx-auto">
                <div className="bg-card p-8 rounded-2xl shadow-xl border border-border">
                  <ComplianceAIResponse
                    response={response}
                    isLoading={isLoading}
                    error={error}
                    onReset={() => {
                      setFormData(null);
                      setResponse(null);
                      setError(null);
                      setTimeRemaining(90);
                    }}
                  />
                </div>
              </div>
            </div>
          </section>
        ) : (
          /* Form Section */
          <div className="max-w-5xl mx-auto mb-16">
            <div className="bg-white dark:bg-gray-800 p-8 rounded-2xl shadow-xl border border-gray-200/80 dark:border-gray-700/80">
              <h2 className="text-2xl font-bold mb-6 text-center">
                Tell us about your AI agent requirements
              </h2>
              <ComplianceAIForm onSubmit={handleFormSubmit} isLoading={isLoading} error={error} />
            </div>
          </div>
        )}
      </main>

      {!isLoading && <Footer />}
    </div>
  );
};

// Feature data
const features = [
  {
    id: 'feature-1',
    title: 'Tailored AI Workflows',
    description:
      'We build custom compliance workflows designed specifically for your industry needs, eliminating overlapping requirements across multiple directives.',
    icon: <Sparkles size={24} />,
  },
  {
    id: 'feature-2',
    title: 'Cost-Effective Automation',
    description:
      'Our platform reduces compliance costs by 65% compared to traditional methods, with pricing up to 45% less than competitors.',
    icon: <BarChart4 size={24} />,
  },
  {
    id: 'feature-3',
    title: 'Enterprise Integration',
    description:
      'Seamlessly integrate with your existing systems with dedicated support and industry-specific AI templates to ensure rapid adoption.',
    icon: <Layers size={24} />,
  },
  {
    id: 'feature-4',
    title: 'Regulatory Intelligence',
    description:
      'Our AI monitors compliance requirements across EU directives, detecting overlaps to streamline implementation and reduce duplicate work.',
    icon: <Shield size={24} />,
  },
  {
    id: 'feature-5',
    title: 'Human-Centered Approach',
    description:
      'Our AI acts as a compliance assistant, not a replacement. We automate repetitive tasks while keeping your team in control of strategic decisions.',
    icon: <Sparkles size={24} />,
  },
  {
    id: 'feature-6',
    title: 'Modular Compliance',
    description:
      'Select from 18 AI-powered compliance components that can be customized to your specific needs and regulatory requirements.',
    icon: <ClipboardCheck size={24} />,
  },
];

// Use case data
const useCases = [
  {
    id: 'usecase-1',
    title: 'Competitive Advantage',
    description:
      'Transform complex EU directives into streamlined processes, helping your compliance team reduce costs by 65%, slash error rates by 86%, and regain 240+ hours annually.',
    badge: 'Cost Savings',
    badgeVariant: 'success',
  },
  {
    id: 'usecase-2',
    title: 'Regulatory Consolidation',
    description:
      'Our platform identifies overlapping requirements across multiple regulations, enabling you to satisfy multiple compliance obligations with unified processes.',
    badge: 'Efficiency',
    badgeVariant: 'destructive',
  },
  {
    id: 'usecase-3',
    title: 'Enterprise-Scale AI',
    description:
      'Custom AI solutions for enterprise compliance needs with pricing that beats competitors by up to 45% while maintaining superior accuracy and regulatory coverage.',
    badge: 'Cost-Effective',
    badgeVariant: 'warning',
  },
];

// Feature component
const Feature = ({
  title,
  description,
  icon,
  index,
}: {
  title: string;
  description: string;
  icon: React.ReactNode;
  index: number;
}) => {
  return (
    <div
      className={`flex flex-col py-8 relative group cursor-pointer border-neutral-200 dark:border-neutral-700 ${
        (index === 0 || index === 3) && 'lg:border-l'
      } ${index < 3 && 'lg:border-b'} ${index % 3 !== 2 && 'lg:border-r'}`}
    >
      {/* Hover effect gradient */}
      <div className="opacity-0 group-hover:opacity-100 transition duration-300 absolute inset-0 h-full w-full bg-gradient-to-r from-transparent via-blue-50/50 dark:via-blue-900/20 to-transparent pointer-events-none" />

      {/* Icon with background */}
      <div className="mb-4 relative z-10 px-10">
        <div className="p-2 rounded-lg bg-blue-50/50 dark:bg-blue-900/20 inline-block">{icon}</div>
      </div>

      {/* Title with animated accent */}
      <div className="text-lg font-bold mb-2 relative z-10 px-10">
        <div className="absolute left-0 inset-y-0 h-6 w-1 rounded-tr-full rounded-br-full bg-neutral-300 dark:bg-neutral-700 group-hover:bg-blue-500 transition duration-200" />
        <span className="group-hover:translate-x-2 transition duration-200 inline-block">
          {title}
        </span>
      </div>

      {/* Description */}
      <p className="text-sm text-neutral-600 dark:text-neutral-300 max-w-xs mx-auto relative z-10 px-10">
        {description}
      </p>
    </div>
  );
};

export default SmallCTAPage;
